/**
 * Browser implementation of Convert Image (canvas + the byte writers in convertCore.ts). Same option names and
 * meanings as the API's POST /v1/image/convert, for the options a browser can do. Not available here (API only):
 * lossless, progressive JPEG, chroma subsampling, PNG compression level and palette, effort, sharpen/blur, and
 * keeping every frame of an animated GIF/WebP. Metadata can be kept for JPEG to JPEG only.
 */
import { decodeImage, canvasToBlob, detectImageKind, hasTransparency } from "./decode";
import {
  computeFit, encodeBmp, encodeGif, encodeIco, encodeTiff, extFor, extractJpegExif, formatInfo, insertJpegExif, resetExifOrientation, setPngDpi,
  type ConvertOptions, type RGBA,
} from "./convertCore";
import { setJpegDpi } from "./jpegDpi";
import { drawResized } from "./operations";
import type { FitMode } from "./convertCore";

const WHITE: RGBA = { r: 255, g: 255, b: 255, a: 1 };
const css = (c: RGBA) => `rgb(${c.r},${c.g},${c.b})`;

function newCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return c;
}
function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const x = c.getContext("2d", { willReadFrequently: true });
  if (!x) throw new Error("This browser could not create a drawing surface for the image.");
  return x;
}

/** JPEG without applying its EXIF rotation flag (so "turn upright" can really be switched off); null when the browser cannot. */
async function decodeAsStored(file: File): Promise<HTMLCanvasElement | null> {
  try {
    if (typeof createImageBitmap !== "function") return null;
    const bmp = await createImageBitmap(file, { imageOrientation: "none" } as ImageBitmapOptions);
    const c = newCanvas(bmp.width, bmp.height);
    ctx2d(c).drawImage(bmp, 0, 0);
    bmp.close?.();
    return c;
  } catch { return null; }
}

/** Rotate (clockwise, multiples of 90), then mirror top-bottom (flip) and left-right (flop). */
export function rotateFlip(src: HTMLCanvasElement, rotate: 0 | 90 | 180 | 270, flip: boolean, flop: boolean): HTMLCanvasElement {
  if (!rotate && !flip && !flop) return src;
  const swap = rotate === 90 || rotate === 270;
  const out = newCanvas(swap ? src.height : src.width, swap ? src.width : src.height);
  const c = ctx2d(out);
  c.translate(out.width / 2, out.height / 2);
  c.rotate((rotate * Math.PI) / 180);
  c.scale(flop ? -1 : 1, flip ? -1 : 1);
  c.drawImage(src, -src.width / 2, -src.height / 2);
  return out;
}

export function toGrayscale(canvas: HTMLCanvasElement): void {
  const c = ctx2d(canvas);
  const img = c.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) { const y = Math.round(0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]); d[i] = d[i + 1] = d[i + 2] = y; }
  c.putImageData(img, 0, 0);
}

function flatten(src: HTMLCanvasElement, bg: RGBA): HTMLCanvasElement {
  const out = newCanvas(src.width, src.height);
  const c = ctx2d(out);
  c.fillStyle = css(bg);
  c.fillRect(0, 0, out.width, out.height);
  c.drawImage(src, 0, 0);
  return out;
}

const bytesOf = async (b: Blob) => new Uint8Array(await b.arrayBuffer());

/**
 * Resizes a canvas the way the API does: same five fit modes (computeFit decides where the picture goes).
 * `bg` fills the padding of "contain" and any see-through areas; without it padding stays see-through.
 * Plain scaling steps down in halves first so big reductions stay sharp.
 */
export function fitCanvas(src: HTMLCanvasElement, width: number | undefined, height: number | undefined, fit: FitMode, withoutEnlargement: boolean, bg?: RGBA): HTMLCanvasElement {
  if (!width && !height) return src;
  const plan = computeFit(src.width, src.height, width, height, fit, withoutEnlargement);
  if (plan.outW === src.width && plan.outH === src.height && plan.dw === src.width) return src;
  const whole = plan.sx === 0 && plan.sy === 0 && plan.sw === src.width && plan.sh === src.height && plan.dx === 0 && plan.dy === 0;
  if (whole && plan.dw === plan.outW && plan.dh === plan.outH) {
    const scaled = drawResized(src, plan.outW, plan.outH);
    return bg ? flatten(scaled, bg) : scaled;
  }
  const out = newCanvas(plan.outW, plan.outH);
  const c = ctx2d(out);
  if (bg) { c.fillStyle = css(bg); c.fillRect(0, 0, out.width, out.height); }
  c.imageSmoothingEnabled = true;
  c.imageSmoothingQuality = "high";
  c.drawImage(src, plan.sx, plan.sy, plan.sw, plan.sh, plan.dx, plan.dy, plan.dw, plan.dh);
  return out;
}

/** Converts one image file with the given (already validated) options. */
export async function convertImageInBrowser(file: File, o: ConvertOptions): Promise<Blob> {
  return (await convertImageDetailed(file, o)).blob;
}

/** Same, and also reports the pixel size of the result (for ICO: the largest icon size). */
export async function convertImageDetailed(file: File, o: ConvertOptions): Promise<{ blob: Blob; width: number; height: number }> {
  const info = formatInfo(o.format);
  const kind = detectImageKind(file);
  let canvas: HTMLCanvasElement | null = null;
  if (!o.autoOrient && kind === "native" && file.type === "image/jpeg") canvas = await decodeAsStored(file);
  if (!canvas) canvas = (await decodeImage(file)).canvas;

  canvas = rotateFlip(canvas, o.rotate, o.flip, o.flop);

  const explicitBg = o.backgroundColor && o.backgroundColor.a > 0 ? o.backgroundColor : undefined;

  canvas = fitCanvas(canvas, o.width, o.height, o.fit, o.withoutEnlargement, explicitBg);
  if (o.grayscale) toGrayscale(canvas);

  // JPEG (and 24-bit BMP) cannot be see-through; a colour chosen for any format fills transparent areas too.
  if (o.format === "jpeg" || explicitBg) canvas = flatten(canvas, explicitBg ?? WHITE);

  let bytes: Uint8Array;
  switch (o.format) {
    case "png":
    case "jpeg":
    case "webp":
    case "avif": {
      const blob = await canvasToBlob(canvas, info.mime, info.lossy ? (o.quality ?? (o.format === "jpeg" ? 92 : o.format === "webp" ? 85 : 60)) / 100 : undefined);
      bytes = await bytesOf(blob);
      if (o.dpi && o.format === "png") bytes = setPngDpi(bytes, o.dpi);
      if (o.dpi && o.format === "jpeg") bytes = setJpegDpi(bytes, o.dpi);
      if (!o.stripMetadata && o.format === "jpeg" && file.type === "image/jpeg") {
        const exif = extractJpegExif(await bytesOf(file));
        if (exif) bytes = insertJpegExif(bytes, o.autoOrient ? resetExifOrientation(exif) : exif);
      }
      break;
    }
    case "gif": {
      const px = ctx2d(canvas).getImageData(0, 0, canvas.width, canvas.height).data;
      bytes = encodeGif(px, canvas.width, canvas.height, { background: explicitBg });
      break;
    }
    case "bmp": {
      const px = ctx2d(canvas).getImageData(0, 0, canvas.width, canvas.height).data;
      bytes = encodeBmp(px, canvas.width, canvas.height, { bits: o.bitDepth, background: explicitBg, dpi: o.dpi });
      break;
    }
    case "tiff": {
      const px = ctx2d(canvas).getImageData(0, 0, canvas.width, canvas.height).data;
      bytes = encodeTiff(px, canvas.width, canvas.height, { alpha: !explicitBg && hasTransparency(canvas), dpi: o.dpi });
      break;
    }
    case "ico": {
      const entries: { size: number; png: Uint8Array }[] = [];
      for (const size of o.sizes) {
        const c = newCanvas(size, size);
        const x = ctx2d(c);
        const s = Math.min(size / canvas.width, size / canvas.height);
        const w = Math.max(1, Math.round(canvas.width * s)), h = Math.max(1, Math.round(canvas.height * s));
        x.imageSmoothingQuality = "high";
        x.drawImage(canvas, Math.round((size - w) / 2), Math.round((size - h) / 2), w, h);
        entries.push({ size, png: await bytesOf(await canvasToBlob(c, "image/png")) });
      }
      bytes = encodeIco(entries);
      break;
    }
  }
  const big = o.format === "ico" ? Math.max(...o.sizes) : 0;
  return { blob: new Blob([bytes as BlobPart], { type: info.mime }), width: big || canvas.width, height: big || canvas.height };
}

export { extFor };
